import React, {
    useState
} from 'react';
import {
    Popover,
    PopoverTrigger,
    PopoverContent
} from 'shared/components/ui/popover';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Slider
} from 'shared/components/ui/slider';
import {
    Button
} from 'shared/components/ui/button';
import {
    Settings2,
    Info
} from 'lucide-react';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from 'shared/components/ui/tooltip';

interface MistralConfigurationParams {
    temperature: number;
    top_p: number;
    frequency_penalty?: number;
    presence_penalty?: number;
    random_seed: number | null;
    max_tokens: number;
    stop?: string[];
    stream: boolean;
}

interface MistralConfigurationProps {
    parameters: MistralConfigurationParams;
    setParameters: (params: MistralConfigurationParams) => void;
}

const MistralConfiguration: React.FC<MistralConfigurationProps> = ({
    parameters,
    setParameters
}) => {
    const [tempLimit] = useState({
        min: 0,
        max: 2
    });
    const [topPLimit] = useState({
        min: 0,
        max: 1
    });
    const [frequencyPenaltyLimit] = useState({
        min: -2.0,
        max: 2.0
    });
    const [presencePenaltyLimit] = useState({
        min: -2.0,
        max: 2.0
    });
    const [seedLimit] = useState({
        min: -2147483648,
        max: 2147483647
    });

    const handleStopChange = (value: string) => {
        if (value.trim()) {
            const sequences = value.split(',').map(sequence => sequence.trim()).filter(Boolean);
            setParameters({
                ...parameters,
                stop: sequences
            });
        } else {
            setParameters({
                ...parameters,
                stop: []
            });
        }
    };

    const getStopValue = (): string => {
        if (Array.isArray(parameters.stop)) {
            return parameters.stop.join(', ');
        }
        return '';
    };

    const InfoIcon: React.FC<{ content: string }> = ({
        content
    }) => (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                    <p className="text-xs">{content}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const renderFieldWithSlider = (
        id: string,
        label: string,
        tooltip: string,
        value: number,
        onChange: (val: number) => void,
        min: number,
        max: number,
        step: number
    ) => (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Label htmlFor={id} className="text-sm">{label}</Label>
                    <InfoIcon content={tooltip} />
                </div>
                <Input
                    id={id}
                    type="number"
                    value={value}
                    step={step}
                    max={max}
                    min={min}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="h-8 w-20"
                />
            </div>
            <Slider
                id={`${id}-slider`}
                value={[value]}
                onValueChange={(val) => onChange(Number(val))}
                step={step}
                max={max}
                min={min}
                className="w-full"
            />
        </div>
    );

    return (
        <Popover>
            <PopoverTrigger asChild>
                <div>
                    <Button aria-haspopup="true" size="icon" variant="ghost">
                        <Settings2 className="h-5 w-5" />
                    </Button>
                </div>
            </PopoverTrigger>

            <PopoverContent className="w-[680px] select-none max-h-[600px] overflow-y-auto" align="center">
                <div className="grid gap-4">
                    <div className="space-y-2">
                        <h4 className="font-medium leading-none text-sm">Configure Mistral Model Parameters</h4>
                        <p className="text-xs text-muted-foreground">
                            Align parameters with the Mistral completion API schema.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="maxTokens" className="text-sm">Max Tokens</Label>
                                    <InfoIcon content="Upper limit for generated tokens. Helps control cost and response length." />
                                </div>
                                <Input
                                    id="maxTokens"
                                    type="number"
                                    value={parameters.max_tokens}
                                    step={1}
                                    min={1}
                                    onChange={(e) => {
                                        setParameters({
                                            ...parameters,
                                            max_tokens: Number(e.target.value)
                                        });
                                    }}
                                    className="h-8"
                                />
                            </div>

                            {renderFieldWithSlider(
                                'temperature',
                                'Temperature',
                                'Controls randomness. Use lower values for deterministic outputs, higher for creativity.',
                                parameters.temperature,
                                (val) => setParameters({
                                    ...parameters,
                                    temperature: val
                                }),
                                tempLimit.min,
                                tempLimit.max,
                                0.1
                            )}

                            {renderFieldWithSlider(
                                'topP',
                                'Top P',
                                'Nucleus sampling threshold. Limits token choices to a cumulative probability mass.',
                                parameters.top_p,
                                (val) => setParameters({
                                    ...parameters,
                                    top_p: val
                                }),
                                topPLimit.min,
                                topPLimit.max,
                                0.05
                            )}

                            {renderFieldWithSlider(
                                'frequencyPenalty',
                                'Frequency Penalty',
                                'Penalizes tokens repeated within the generation. Positive values reduce repetition.',
                                parameters.frequency_penalty ?? 0,
                                (val) => setParameters({
                                    ...parameters,
                                    frequency_penalty: val
                                }),
                                frequencyPenaltyLimit.min,
                                frequencyPenaltyLimit.max,
                                0.1
                            )}
                        </div>

                        <div className="space-y-4">
                            {renderFieldWithSlider(
                                'presencePenalty',
                                'Presence Penalty',
                                'Encourages new topics by penalizing tokens that already appeared.',
                                parameters.presence_penalty ?? 0,
                                (val) => setParameters({
                                    ...parameters,
                                    presence_penalty: val
                                }),
                                presencePenaltyLimit.min,
                                presencePenaltyLimit.max,
                                0.1
                            )}

                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="stopSequences" className="text-sm">Stop Sequences</Label>
                                    <InfoIcon content="Comma-separated strings that terminate generation once encountered." />
                                </div>
                                <Input
                                    id="stopSequences"
                                    type="text"
                                    value={getStopValue()}
                                    onChange={(e) => handleStopChange(e.target.value)}
                                    placeholder="e.g., \n\n, </END>"
                                    className="h-8"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="randomSeed" className="text-sm">Random Seed</Label>
                                    <InfoIcon content="Use a seed to reproduce identical outputs. Leave blank for true randomness." />
                                </div>
                                <Input
                                    id="randomSeed"
                                    type="number"
                                    value={parameters.random_seed ?? ''}
                                    step={1}
                                    max={seedLimit.max}
                                    min={seedLimit.min}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setParameters({
                                            ...parameters,
                                            random_seed: value === '' ? null : Number(value)
                                        });
                                    }}
                                    placeholder="None"
                                    className="h-8"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor="stream" className="text-sm">Stream Output</Label>
                                        <InfoIcon content="Enable SSE streaming to receive tokens as they are generated." />
                                    </div>
                                    <Switch
                                        id="stream"
                                        checked={parameters.stream}
                                        onCheckedChange={(checked) => {
                                            setParameters({
                                                ...parameters,
                                                stream: checked
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default MistralConfiguration;
